import React, { useContext, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "./UserContext";
import { useTheme } from "./ThemeContext";
import { Menu, Sun, Moon, PencilLine } from "lucide-react";

function Navbar() {
  const { userInfo, setUserInfo } = useContext(UserContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  function handleLogout() {
    fetch(`${process.env.REACT_APP_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
    }).then(() => {
      setUserInfo({});
      setDropdownOpen(false);
      setMobileMenuOpen(false);
      navigate("/");
    });
  }

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const wrapClasses = theme === 'dark'
    ? 'rounded-2xl border border-white/10 bg-white/5'
    : 'rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur'
  const iconBtn = theme === 'dark'
    ? 'border border-white/10 bg-white/5 text-white/80 hover:bg-white/10'
    : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
  const brandText = theme === 'dark' ? 'text-white' : 'text-slate-800'
  const navLink = theme === 'dark' ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-slate-900'
  const cta = theme === 'dark'
    ? 'border border-white/10 bg-white/5 text-white/90 hover:bg-white/10'
    : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'

  return (
    <header className="fixed top-0 left-0 right-0 z-30 backdrop-blur-sm">
      <div className="mx-auto max-w-6xl px-4">
        <div className={`mt-4 flex items-center justify-between p-3 ${wrapClasses}`}>
          <div className="flex items-center gap-3">
            <button
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${iconBtn} md:hidden`}
              onClick={() => setMobileMenuOpen(v => !v)}
            >
              <Menu size={18} />
            </button>
            <Link to="/" className={`flex items-center gap-2 font-semibold ${brandText}`}>
              <span>BlogWeb</span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {!userInfo.username && (
              <nav className="hidden items-center gap-6 text-sm md:flex">
                <Link to="/login" className={navLink}>Login</Link>
                <Link to="/register" className={navLink}>Register</Link>
              </nav>
            )}

            {userInfo.username && (
              <>
                <Link to="/create" className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 ${cta}`}>
                  <PencilLine size={16} />
                  <span className="hidden sm:inline">Write</span>
                </Link>
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen((v) => !v)}
                    className="flex items-center gap-2"
                  >
                    <span className={`font-semibold ${navLink}`}>{userInfo.username}</span>
                    <span className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50 animate-fade-in">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 transition"
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Profile
                      </Link>
                      <Link
                        to="/my-blog"
                        className="block px-4 py-2 text-gray-700 hover:bg-blue-50 transition"
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Blog
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-red-600 hover:bg-blue-50 rounded-b-lg transition"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${iconBtn}`}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`mt-2 rounded-xl border p-4 ${theme === 'dark' ? 'border-white/10 bg-white/5' : 'border-slate-200/80 bg-white/80'} md:hidden`}>
            <nav className="flex flex-col gap-4">
              {!userInfo.username && (
                <>
                  <Link to="/" className={navLink} onClick={() => setMobileMenuOpen(false)}>Home</Link>
                  <Link to="/login" className={navLink} onClick={() => setMobileMenuOpen(false)}>Login</Link>
                  <Link to="/register" className={navLink} onClick={() => setMobileMenuOpen(false)}>Register</Link>
                </>
              )}
               {userInfo.username && (
                <>
                  <Link to="/create" className={navLink} onClick={() => setMobileMenuOpen(false)}>Create</Link>
                  <Link to="/profile" className={navLink} onClick={() => setMobileMenuOpen(false)}>My Profile</Link>
                  <Link to="/my-blog" className={navLink} onClick={() => setMobileMenuOpen(false)}>My Blog</Link>
                  <button onClick={handleLogout} className={`${navLink} text-left`}>Logout</button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default Navbar;